import { usePageData } from "../../../contexts/PageData";
import styled from "@emotion/styled";

import { Link } from "react-router-dom";

import { Grid, Card, CardHeader, CardContent, CardActions, Button, Typography, Divider, IconButton, Tooltip, lighten } from "@mui/material";

import { CardBlock, CardBlockHeader, CardBlockContainer, CardSpacer } from "../../../utils/utilities/CardComponents";

import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import HideSourceRoundedIcon from '@mui/icons-material/HideSourceRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';

import AddIcon from '@mui/icons-material/AddRounded';

import useSnackbarStore from "../../../store/snackbar";

import { BasicFlex } from "../../../utils/Common";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { getAppliedPayments } from "../apiService";

import { removeAppliedPayment } from "../../Payments/apiService";

import MultiStatusChip from "../../../utils/utilities/MultiStatusChip";

import { formatCurrency, formatGenericDate } from "../../../utils/util";

import { methods } from "../../Payments/helpers";

import NoItemsFound from "../../../utils/NoItems";

const PaymentTileStyles = styled(BasicFlex)`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  /* margin: 1rem 0; */
  background-color: white;
  align-items: stretch;
  min-height: 10rem;
  margin-bottom: 1.5rem;
`;

const AppliedInfo = styled(BasicFlex)`
 /* min-width: 20rem; */
 background-color: ${props => lighten(props.theme.colors.secondaryFaint, 0.3)};
 padding: 2rem;
 gap: 1rem;
 max-width: 25rem;
`;

/* applied styles */
const AppliedAmount = styled.div`
  font-weight: 600;
  font-size: 2.6rem;
  color: ${props => props.theme.colors.mildText};
`;

const Subheader = styled(Link)`
  font-weight: 500;
  font-size: 1.4rem;
  color: ${props => props.theme.colors.mildText};
  text-decoration: none;
`;

const Decorated = styled.span`
  text-decoration: underline;
`

const AppliedDate = styled(BasicFlex)`
  font-size: 1.4rem;
  width: fit-content;
`;
/* end applied styles */

const OriginalInfo = styled(BasicFlex)`
  flex-grow: 1;
  padding: 1rem;
`; 

/* original styles */
const OriginalHeader = styled.div`
  font-weight: 500;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const OriginalDetails = styled(BasicFlex)`
  flex-grow: 1;
`;

const OriginalAmount = styled(BasicFlex)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
  width: auto;
  min-width: fit-content;
  padding: 0 2rem;
  height: 100%;
  gap: .2rem;

  div {
    width: fit-content;
  }

  .amount {
    font-size: 1.8rem;
    font-weight: 600;
  }
`;

const OriginalPaymentDetails = styled(BasicFlex)`
  flex-direction: column;
  gap: 1rem;
  flex-wrap: wrap;
  max-height: 10rem;
  margin-left: 2rem;
`;

const SmallTileStyles = styled(BasicFlex)`
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.lightBg};
  padding: .2rem .6rem;
  border-radius: 5px;
  width: fit-content;
  font-size: 1.2rem;
  gap: .6rem;
`;

const SmallTileHeader = styled.div`
  color: ${props => props.theme.colors.mildText};
  text-transform: uppercase;
`;

const SmallTileInfo = styled.div`
  font-weight: 500;
`;
/* end original styles */

const Actions = styled(BasicFlex)`
  gap: 1rem;
  width: 10rem;
`;


const SmallTile = ({Icon, header, info}) => {
  return (
    <SmallTileStyles>
      {Icon ? <div><Icon fontSize="small" /></div> : null}
      <BasicFlex dir="column">
        <SmallTileHeader>{header}</SmallTileHeader>
        <SmallTileInfo>{info}</SmallTileInfo>
      </BasicFlex>
    </SmallTileStyles>
  )
}

const PaymentTile = ({payment = {}}) => {

  const { amount = 200, date = new Date(), method = "credit_card", info = "4567", display_amount, orig_payment_date, orig_payment_amount} = payment;

  let payMethod = methods.find((m) => m.value === method);

  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { mutate: removePayment } = useMutation({
    mutationFn: () => removeAppliedPayment(payment.applied_payment_id),
    onSuccess: () => {
      showSnackbar('Removed successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });

  return (
    <PaymentTileStyles>
      <AppliedInfo dir="column" align="center">
        <AppliedAmount>{formatCurrency(amount)}</AppliedAmount>
        <Subheader to={`/invoices/view/${payment.invoice_id}`}>Applied To Invoice <Decorated>#{payment.invoice_num}</Decorated></Subheader>
        <AppliedDate gap="1rem"><CalendarMonthRoundedIcon /> {formatGenericDate(date)}</AppliedDate>
      </AppliedInfo>
      <OriginalInfo dir="column">
        <OriginalHeader>Original Payment</OriginalHeader>
        <OriginalDetails justify="flex-start" align="flex-start">
          <OriginalAmount dir="column" justify="center" align="center">
            <div className="amount">{formatCurrency(orig_payment_amount)}</div>
            <div>Original Amount</div>
          </OriginalAmount>
          <Divider orientation="vertical" flexItem sx={{mx: 1}} />
          <OriginalPaymentDetails align="flex-start">

            <SmallTile header="Method" info={payMethod?.label} Icon={payMethod?.Icon} />
            <SmallTile header="Last 4" info={info} Icon={HideSourceRoundedIcon} />
           

          </OriginalPaymentDetails>
        </OriginalDetails>
      </OriginalInfo>
      <Actions>
        <Tooltip title="Unlink Payment from Invoice">
          <IconButton size="large" color="primary" onClick={removePayment}>
            <LinkOffRoundedIcon />
          </IconButton>
        </Tooltip>
      </Actions>
    </PaymentTileStyles>
  )

}

const AppliedPayments = ({ payments = []}) => {
  const { data = {} } = usePageData();

  let customer = data?.customer || {};

  return (
    payments?.length ? payments?.map((payment, i) => (
        <PaymentTile key={i} payment={payment} />
      )) : <NoItemsFound title="No applied payments found" />
  )
};

export default AppliedPayments;
